
















import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  BulletListNode,
  CommentHighlights,
  HardBreakNode,
  HeadingLevel,
  HeadingNode,
  ListItemNode,
  MergeValueNode,
  NodeAlign,
  OrderedListNode,
  ParagraphNode,
  ProsemirrorType,
  TextAlign,
  TextDocNode,
} from '@getaccept/editor-lib';
import type { ProseMirrorSelection } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-selection';
import { SelectionType } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-selection';
import type { TextContentDoc } from '@getaccept/lib-shared/src/types/text-content';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Superscript from '@tiptap/extension-superscript';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import type { EditorOptions } from '@tiptap/vue-2';
import { EditorContent, Editor as TipTapEditor } from '@tiptap/vue-2';
import type { PropType, Ref } from '@vue/composition-api';
import {
  toRef,
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared/src/contextual-commenting/types/create-contextual-comment-button-state';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { useContextualCommentingStore } from '@getaccept/lib-shared/src/contextual-commenting/store/contextual-commenting.store';
import { storeToRefs } from 'pinia';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';
import { useCreateContextualCommentButton } from '../composables/create-contextual-comment-button';
import { handleInit, setContent, updateMergeValues } from './helpers/prosemirror-viewer.helper';
import CreateCommentButton from './CreateCommentButton.vue';

export default defineComponent({
  name: 'ProsemirrorViewerText',
  components: {
    EditorContent,
    CreateCommentButton,
  },
  props: {
    defaultHeading: {
      type: Number as PropType<HeadingLevel>,
      default: () => HeadingLevel.Heading1,
    },
    defaultAlign: { type: String as PropType<NodeAlign>, default: () => NodeAlign.Left },
    content: {
      type: [Object, String] as PropType<TextContentDoc | string>,
      default: '',
    },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    fields: { type: Array as PropType<Field[]>, default: () => [] },
    comments: { type: Array as PropType<Comment[]>, default: () => [] },
    selectedCommentId: { type: String, default: '' },
    showContextualComments: { type: Boolean, default: false },
    createContextualCommentButtonState: {
      type: String as PropType<CreateContextualCommentButtonState>,
      default: CreateContextualCommentButtonState.Enabled,
    },
  },
  setup(props, { emit }) {
    const viewer: Ref<TipTapEditor | null> = ref(null);
    const editorContainer = ref(null);
    const isEditorReady = ref(false);

    const getCoordsAtSelectionEnd = () => {
      if (!viewer.value?.view) {
        return null;
      }

      const { to } = viewer.value.view.state.selection;
      return viewer.value.view.coordsAtPos(to);
    };

    const {
      showCreateCommentButton,
      calculatePosition: calculateCreateCommentButtonPosition,
      hideButton: hideCreateCommentButton,
      buttonPosition: createCommentButtonPosition,
    } = useCreateContextualCommentButton(
      toRef(props, 'showContextualComments'),
      editorContainer,
      getCoordsAtSelectionEnd
    );

    const ccStore = useContextualCommentingStore();

    const { hoveredCommentId, previouslySelectedCommentId } = storeToRefs(ccStore);

    const canCreateComment = computed(
      () =>
        props.showContextualComments &&
        props.createContextualCommentButtonState === CreateContextualCommentButtonState.Enabled
    );

    const contentPayload = computed(() => ({
      viewer: viewer.value,
      content: props.content,
      fields: props.fields,
      mergeValues: props.mergeValues,
    }));

    const textViewer = computed<Partial<EditorOptions>>(() => ({
      content: props.content,
      editable: false,
      enableInputRules: false,
      enablePasteRules: false,
      extensions: [
        TextDocNode,
        ParagraphNode,
        Text,
        Bold,
        Italic,
        Strike,
        Superscript,
        Underline,
        OrderedListNode,
        CommentHighlights.configure({
          onCursorIntersection: payload => {
            const { commentId, from } = payload;
            if (!commentId) {
              return;
            }
            selectComment(commentId, from);
          },
        }),
        BulletListNode,
        ListItemNode,
        MergeValueNode,
        Link.configure({
          autolink: false,
          openOnClick: true,
          linkOnPaste: false,
        }),
        HeadingNode.configure({
          defaultLevel: props.defaultHeading,
        }),
        HardBreakNode,
        TextAlign.configure({
          defaultAlignment: props.defaultAlign,
          types: [
            ProsemirrorType.Paragraph,
            ProsemirrorType.Heading,
            ProsemirrorType.BulletList,
            ProsemirrorType.OrderedList,
          ],
        }),
      ],
      onCreate: () => {
        handleInit(viewer.value, props.fields, props.mergeValues);
        isEditorReady.value = true;
      },
      onSelectionUpdate: calculateCreateCommentButtonPosition,
    }));

    const cursorStyle = computed(() => {
      if (!canCreateComment.value) {
        return '';
      }

      const contextualCommentTextCursorSVG =
        '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';

      return `cursor: url('data:image/svg+xml;base64,${contextualCommentTextCursorSVG}') 5 12, text;`;
    });

    const isShowingCommentHighlights = computed(
      () => !!props.comments.length && props.showContextualComments && isEditorReady.value
    );

    watch(
      () => props.comments,
      (newValue, oldValue) => {
        if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
          return;
        }
        viewer.value.commands.updateCommentHighlights(props.comments);
      }
    );

    watch(isEditorReady, (newValue: boolean, oldValue: boolean) => {
      if (!newValue || (newValue && oldValue)) {
        return;
      }
      if (props.showContextualComments && props.comments.length) {
        viewer.value.commands.updateCommentHighlights(props.comments);
        if (props.selectedCommentId) {
          viewer.value.commands.selectCommentHighlight(props.selectedCommentId);
        }
      }
      ccStore.setEditorHasRendered();
    });

    watch(isShowingCommentHighlights, () => {
      if (isShowingCommentHighlights.value) {
        viewer.value.commands.showCommentHighlights(props.comments);
        return;
      }
      viewer.value.commands.hideCommentHighlights();
    });

    watch(
      () => props.selectedCommentId,
      () => {
        viewer.value.commands.selectCommentHighlight(props.selectedCommentId);
        hideCreateCommentButton();
      }
    );

    watch(hoveredCommentId, () =>
      viewer.value.commands.hoverCommentHighlight(hoveredCommentId.value)
    );

    const createComment = () => {
      const { selection } = viewer.value.view.state;
      const { head, anchor } = selection.toJSON();
      const editor = viewer.value;
      if (!editor) return;

      const content = JSON.stringify(
        editor.view.state.doc.cut(selection.from, selection.to).toJSON()
      );

      const textSelection: ProseMirrorSelection = {
        head,
        anchor,
        type: SelectionType.Text,
      };

      hideCreateCommentButton();

      emit('create-comment', {
        textSelection,
        content,
      });
    };

    onMounted(() => {
      viewer.value = new TipTapEditor(textViewer.value);
    });

    onBeforeUnmount(() => {
      viewer.value.destroy();
    });

    watch(
      () => [props.fields, props.mergeValues],
      () => {
        updateMergeValues(contentPayload.value);
      }
    );

    watch(
      () => props.content,
      () => {
        setContent(contentPayload.value);
      }
    );

    const selectComment = (commentId: string, from: number) => {
      const hasNoOverlappingComments = CommentsHelper.hasNoOverlappingComments(
        props.comments,
        from
      );
      if (hasNoOverlappingComments || commentId !== previouslySelectedCommentId.value) {
        emit('select-comment', commentId);
        return;
      }
      const nextCommentIdToBeSelected = CommentsHelper.getNextCommentIdToBeSelected(
        props.comments,
        commentId,
        from
      );
      emit('select-comment', nextCommentIdToBeSelected);
    };

    return {
      createComment,
      cursorStyle,
      viewer,
      editorContainer,
      showCreateCommentButton,
      createCommentButtonPosition,
    };
  },
});
